import classNames from 'classnames';
import { memo, useEffect, useState } from 'react';

import Lottie from '@/components/LottiePlayer';
import DotsLoading from '@/components/dots-loading';

import styles from './index.less';

// CDN URL for the animation JSON
const ANIMATION_CDN_URL =
  'https://mindverseglobal.mindverse.com/front-img/assets/1761551075609/chat-loading.json';

// Cache for animation data (shared across all instances)
let cachedAnimationData: any = null;
let loadingPromise: Promise<any> | null = null;

// Preload animation data from CDN
const loadAnimationData = () => {
  if (cachedAnimationData) {
    return Promise.resolve(cachedAnimationData);
  }

  if (loadingPromise) {
    return loadingPromise;
  }

  loadingPromise = fetch(ANIMATION_CDN_URL)
    .then((response) => {
      if (!response.ok) {
        throw new Error('Failed to load animation');
      }

      return response.json();
    })
    .then((data) => {
      cachedAnimationData = data;
      loadingPromise = null;

      return data;
    })
    .catch((error) => {
      console.warn('Failed to load Lottie animation from CDN:', error);
      loadingPromise = null;

      // Fallback: use local import as backup
      return import('@/assets/chat-loading.json').then((module) => {
        cachedAnimationData = module.default;

        return module.default;
      });
    });

  return loadingPromise;
};

// Start preloading on module load (non-blocking, safe in SSR since fetch is available)
loadAnimationData();

interface IProps {
  className?: string;
}

function LottieJumpLoading({ className }: IProps): JSX.Element {
  const [animationData, setAnimationData] = useState<any>(cachedAnimationData);

  useEffect(() => {
    if (!animationData) {
      loadAnimationData().then((data) => {
        setAnimationData(data);
      });
    }
  }, [animationData]);

  if (!animationData || !Lottie) {
    return <DotsLoading />;
  }

  return (
    <Lottie
      animationData={animationData}
      autoPlay
      className={classNames(styles.lottieLoading, className)}
      loop
    />
  );
}

export default memo(LottieJumpLoading);
